import { UzSelect } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { useAgentOptionsForLeadsWithRole } from '../use-lead-options';

import { useCurrentUser } from '@/features/auth/use-auth';

// Module-level constants (kept out of the component so useMemo deps stay
// referentially stable across renders).
const SALES_ROLES: ReadonlyArray<string> = ['SALES_LEAD', 'SALES_AGENT'];
const ROLE_RANK: Record<string, number> = { SALES_LEAD: 0, SALES_AGENT: 1 };
const roleLabel = (role: string): string =>
  role === 'SALES_LEAD' ? 'Team lead' : role === 'SALES_AGENT' ? 'Sales agent' : role;

/**
 * Role-scoped owner filter for the lead inbox. Sits in the page-shell
 * actions and controls the `assignedAgentId` URL param so every child
 * (the pipeline tiles, the trackers, the KPI strip, the All-leads
 * DataGrid) re-queries with the same filter.
 *
 * Populated from `/users/assignable-agents` (already hierarchy-scoped
 * server-side):
 *   - ADMIN         -> every active SALES_LEAD + SALES_AGENT, grouped
 *                       "Team leads" (SALES_LEAD) then "Sales agents"
 *                       (SALES_AGENT)
 *   - SALES_LEAD    -> self + their direct reports (SALES_AGENT) - the
 *                       backend already scopes for us, so the same
 *                       grouping renders naturally
 *   - SALES_AGENT   -> only self is returned; we hide the dropdown so
 *                       the surface stays clean (they can only see
 *                       their own leads anyway)
 *
 * The empty string is the "All owners" reset - matches how every other
 * URL-param-driven filter in the leads inbox behaves.
 */
export const OwnerFilter = ({
  value,
  onValueChange,
  className = 'w-52',
  triggerClassName,
}: {
  value: string | null | undefined;
  onValueChange: (next: string | null) => void;
  /** Wrapper width/layout override. Defaults to the inbox header's fixed w-52;
   *  the detail-page aside passes `w-full` so it fills the narrow column. */
  className?: string;
  /** Select-trigger override (e.g. the aside's compact `h-8 text-xs`). */
  triggerClassName?: string;
}): React.JSX.Element | null => {
  const me = useCurrentUser();
  const { data: agents, isLoading } = useAgentOptionsForLeadsWithRole();

  // Sales agents only see themselves - dropdown adds no value.
  const isSalesAgent = me?.role === 'SALES_AGENT';

  // Role-first ordering: SALES_LEADs at the top, SALES_AGENTs below,
  // "All owners" pinned at the very top. UzSelect doesn't natively
  // support option groups, so we surface the role via each option's
  // `description` (the picker renders it under the label at a smaller
  // font) and order by role-rank so the visual hierarchy is obvious.
  // 2026-07-09. Explicit whitelist on the roles: only sales users
  // (SALES_LEAD + SALES_AGENT) belong in this filter. The upstream
  // /users/assignable-agents endpoint is already scoped that way today,
  // but the client-side filter guarantees that if a future backend
  // widening adds OPS / FINANCE rows they won't leak into the owner
  // dropdown. SALES_ROLES / ROLE_RANK / roleLabel live at module scope
  // above, so useMemo deps stay narrow and stable.
  const options = useMemo(() => {
    const rows = [...agents]
      .filter((a) => SALES_ROLES.includes(a.role))
      .sort((a, b) => {
        const ra = ROLE_RANK[a.role] ?? 99;
        const rb = ROLE_RANK[b.role] ?? 99;
        if (ra !== rb) return ra - rb;
        return a.fullName.localeCompare(b.fullName);
      });
    return [
      { value: '', label: 'All owners' },
      ...rows.map((a) => ({
        value: a.id,
        label: a.fullName,
        description: roleLabel(a.role),
      })),
    ];
  }, [agents]);

  if (isSalesAgent) return null;
  // While the roster is still loading show a disabled skeleton so the
  // layout doesn't jump when the options come in.
  return (
    <div className={className}>
      <UzSelect<string>
        name="ownerFilter"
        triggerClassName={triggerClassName}
        placeholder={isLoading ? 'Loading owners…' : 'All owners'}
        options={options}
        value={value ?? ''}
        onValueChange={(v) => onValueChange(v ? v : null)}
        disabled={isLoading}
        descriptionRight
        // Rosters run long (every team lead + agent) - cap the open menu
        // at ~7 rows and let it scroll, instead of the 96-unit default
        // that swallows half the panel.
        contentClassName="max-h-60"
      />
    </div>
  );
};
